import React, { type FC, useRef, useState, useCallback } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import classNames from 'classnames';

import Modal from '../AlertModal/Modal';

import type { Location } from './interfaces';
import './Breadcrumbs.scss';
import { useAppDispatch } from '../../app/hooks';
import { clearFilter } from '../../features/filter/filterSlice';
import { econdaClickEvent } from '../../Econda';
import useHeadline from '../Headline/hooks/useHeadline';
import { RequestHandler } from '../../app/RequestHandler';
import useTranslation from '../../features/translation/hooks/useTranslation';
import useFoxPresence from '../Headline/hooks/useFoxPresence';

export interface BreadcrumbProps {
  locations: Location[];
}

enum TranslationKeys {
  GRIPPER_SIZING_TOOL = 'headerMain',
}

// TO-DO: Add Breadcrumbs.test.tsx to test the behavior

const Breadcrumbs: FC<BreadcrumbProps> = ({ locations }) => {
  const { pathname } = useLocation();
  const dispatch = useAppDispatch();
  const t = useTranslation();
  const [modalOpen, setModalOpen] = useState(false);
  const linkRef = useRef<HTMLAnchorElement>(null);
  const { isResultPage } = useHeadline();
  const { applicationType } = new RequestHandler();
  const isStandalone = applicationType === 'standalone';
  const { hasFoXBreadcrumbs } = useFoxPresence();

  function resetApplication(e: React.MouseEvent) {
    e.preventDefault();
    econdaClickEvent(pathname, 'general', 'breadcrumbs', 'click');
    setModalOpen(true);
  }

  const handleOk = useCallback(() => {
    econdaClickEvent(pathname, 'general', 'warning modal', 'ok');
    setModalOpen(false);
    if (linkRef.current) {
      linkRef.current.click();
    }
    dispatch(clearFilter());
  }, [dispatch]);

  // TODO: uncomment if there is any problem, remove if everything is okay
  // useEffect(() => {
  //   const pathArray = pathname.split('/');
  //   if (navigationType === 'POP' && !isFirstPage && pathArray[1] !== '' && isLoginRedirect()) {
  //     if (!isResultPage) {
  //       const motionPath = pathArray[1];
  //       navigate(motionPath);
  //     } else {
  //       const resultPage = pathArray.join('/');
  //       navigate(resultPage);
  //     }
  //     setModalOpen(true);
  //   }
  // }, [handleOk, isFirstPage, isResultPage, modalOpen, navigate, pathname, navigationType]);

  const onCloseModal = () => {
    setModalOpen(false);
  };

  const onCancel = () => {
    econdaClickEvent(pathname, 'general', 'warning modal', 'cancel');
    setModalOpen(false);
  };

  const homePageStandaloneBreadcrumbs = isResultPage ? (
    <NavLink
      to=""
      onClick={resetApplication}
      className={isResultPage ? 'fng-breadcrumb-location' : 'fng-breadcrumb-location-true'}
    >
      {t(TranslationKeys.GRIPPER_SIZING_TOOL)}
    </NavLink>
  ) : null;

  if (hasFoXBreadcrumbs) return <></>;

  return (
    <div className="container-fluid fwe-breadcrumbs-container fwe-d-flex">
      <Modal isOpen={modalOpen} onCancel={onCancel} onClose={onCloseModal} onOk={handleOk} />
      {isStandalone ? (
        homePageStandaloneBreadcrumbs
      ) : (
        <>
          <NavLink ref={linkRef} to="/" />

          <div className="fwe-breadcrumb">
            {locations.map((location, index) => {
              const classes = classNames('fwe-d-sm-block', { 'fwe-d-none': index !== locations.length - 2 });
              const arrowLeftClasses = classNames('fwe-d-sm-none', { 'fwe-d-block': index !== locations.length - 2 });

              return (
                <React.Fragment key={location.label + location.url}>
                  {index === locations.length - 2 && (
                    <i className={`${arrowLeftClasses} fwe-icon fwe-icon-arrows-left-2`} />
                  )}

                  {index < 2 && (
                    <a
                      target="_blank"
                      rel="noreferrer"
                      className={`${classes} fng-breadcrumb-location`}
                      href={location.url}
                    >
                      {location.label}
                    </a>
                  )}

                  {index === 2 && (
                    <NavLink to={'/'} onClick={resetApplication} className={`${classes} fng-breadcrumb-location`}>
                      {location.label}
                    </NavLink>
                  )}

                  {index > 2 && (
                    <NavLink to={location.url} className={`${classes} fng-breadcrumb-location`}>
                      {location.label}
                    </NavLink>
                  )}

                  {index + 1 < locations.length && (
                    <i className="fwe-d-sm-block fwe-d-none fwe-icon fwe-icon-arrows-right-2" />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default Breadcrumbs;
